/**
 *
 * Revenue.io Calculator v2
 *
 */

import { useState, useEffect, useRef } from "react";
import { createComponent, useFela } from "react-fela";
import { colors, gutters } from "~/styleguide";
import { fluid } from "~/styleguide/utils";
import { textStyles, fonts } from "~/styleguide/typography";
import { ContentMaxWidth } from "~/components/layout/max-width";

/* ================
   DOWNLOAD PDF
   ================ */
const downloadBytes = (bytes, filename, mime = "application/pdf") => {
  const blob = new Blob([bytes], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

async function fillForm(calculatorresults, calculatorinputs) {
  try {
    const { PDFDocument, StandardFonts } = await import("pdf-lib");
    const {
      totalteamannualopportunity,
      additionalrevenueperrepannually,
      hourssavedperrepperweek,
      currenthourslostperweek,
      currentunrealizedrevenueannually,
      currentequivalentftesdoingadmin,
      revenueairecoveredrevenueannually,
      revenueaigrowthwithoutnewhires,
      hourssavedperweek,
      revenueperhour,
      weeksperyear,
      convertedtosellingtime,
      perrep,
      whatthismeans,
      timetovalue,
      weeklyopportunitycost,
    } = calculatorresults;

    const {
      numberofreps,
      yearlyrevenueperrep,
      meetingsperweek,
      mintuesnotes,
      mintuescrm,
      mintuesemail,
      mintuesreviewingcalls,
      mintuescoaching,
      timesavedactivities,
    } = calculatorinputs;

    const formUrl = "/revenue-calculator-v2.pdf";
    const formPdfBytes = await fetch(formUrl).then(res => res.arrayBuffer());

    const pdfDoc = await PDFDocument.load(formPdfBytes);
    const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    const pdfForm = pdfDoc.getForm();

    // Example mappings:
    pdfForm.getTextField("totalteamannualopportunity")
      .setText(`$${Math.round(totalteamannualopportunity).toLocaleString("en-US")}`);

    pdfForm.getTextField("additionalrevenueperrepannually")
      .setText(`$${Math.round(additionalrevenueperrepannually).toLocaleString("en-US")}`);

    pdfForm.getTextField("hourssavedperrepperweek")
      .setText(hourssavedperrepperweek.toFixed(1));

    pdfForm.getTextField("currenthourslostperweek")
      .setText(String(currenthourslostperweek));

    pdfForm.getTextField("currentunrealizedrevenueannually")
      .setText(`${(currentunrealizedrevenueannually / 1000000).toFixed(1)}M`);

    pdfForm.getTextField("currentequivalentftesdoingadmin")
      .setText(String(currentequivalentftesdoingadmin));

    // With Revenue AI
    pdfForm.getTextField("revenueairecoveredrevenueannually")
      .setText(`${(revenueairecoveredrevenueannually / 1000000).toFixed(1)}M`);

    pdfForm.getTextField("revenueaihourssavedperrepweek")
      .setText(hourssavedperrepperweek.toFixed(1));

    pdfForm.getTextField("revenueaigrowthwithoutnewhires")
      .setText(`${revenueaigrowthwithoutnewhires}%`);

    // Calculations
    pdfForm.getTextField("hourssavedperweek")
      .setText(hourssavedperweek.toFixed(1));

    pdfForm.getTextField("revenueperhour")
      .setText(String(Math.round(revenueperhour)));

    pdfForm.getTextField("weeksperyear")
      .setText(String(weeksperyear));

    pdfForm.getTextField("convertedtosellingtime")
      .setText(`${convertedtosellingtime}%`);

    pdfForm.getTextField("perrep")
      .setText(`$${Math.round(perrep).toLocaleString("en-US")}`);

    // Insights
    pdfForm.getTextField("whatthismeans")
      .setText(String(whatthismeans));

    pdfForm.getTextField("timetovalue")
      .setText(String(timetovalue));

    pdfForm.getTextField("weeklyopportunitycost")
      .setText(String(weeklyopportunitycost));

    // Inputs
    pdfForm.getTextField("meetingsperweek")
      .setText(String(meetingsperweek));

    pdfForm.getTextField("mintuesnotes")
      .setText(String(mintuesnotes));

    pdfForm.getTextField("mintuescrm")
      .setText(String(mintuescrm));

    pdfForm.getTextField("mintuesemail")
      .setText(String(mintuesemail));

    pdfForm.getTextField("mintuesreviewingcalls")
      .setText(String(mintuesreviewingcalls));

    pdfForm.getTextField("mintuescoaching")
      .setText(String(mintuescoaching));

    pdfForm.getTextField("numberofreps")
      .setText(String(numberofreps));

    pdfForm.getTextField("yearlyrevenueperrep")
      .setText(String(yearlyrevenueperrep));

    pdfForm.getTextField("timesavedactivities")
      .setText(String(timesavedactivities));

    pdfForm.updateFieldAppearances(helveticaBold);
    pdfForm.getTextField("totalteamannualopportunity").updateAppearances(helveticaBold);
    pdfForm.getTextField("additionalrevenueperrepannually").updateAppearances(helveticaBold);
    pdfForm.getTextField("hourssavedperrepperweek").updateAppearances(helveticaBold);

    pdfForm.flatten();

    const pdfBytes = await pdfDoc.save();
    downloadBytes(pdfBytes, "revenue-calculator.pdf");
  } catch (err) {
    console.error("Failed to fill/download PDF:", err);
  }
}

    let caculatorFormInstanceCount = 0;

/* ================
   MAIN COMPONENT
   ================ */

export default function RevenueCalculator({ block }) {

  const { anchor_id, form_id } = block;

  // Form state
  const [numReps, setNumReps] = useState(50);
  const [revenuePerRep, setRevenuePerRep] = useState(750000);
  const [meetingsPerWeek, setMeetingsPerWeek] = useState(18);
  const [noteTime, setNoteTime] = useState(10);
  const [crmTime, setCrmTime] = useState(8);
  const [emailTime, setEmailTime] = useState(12);
  const [reviewTime, setReviewTime] = useState(15);
  const [coachingTime, setCoachingTime] = useState(60);
  const [recoveryRate, setRecoveryRate] = useState(75);

  // UI state
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([1]);
  const [showResults, setShowResults] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const totalSteps = 3;

  // Calculations
  // Replace in v2 (Calculations block)
  const HOURS_PER_YEAR = 2040;              // single source of truth
  const rph = revenuePerRep / HOURS_PER_YEAR; // ← no Math.round here
  const industryAvg = 287;

  const timePerMeeting = noteTime + crmTime + emailTime + reviewTime;
  const weeklyTimeLostPerRep =
    (meetingsPerWeek * timePerMeeting + coachingTime) / 60;
  const totalWeeklyTimeLost = weeklyTimeLostPerRep * numReps;
  const weeklyRevenueLost = totalWeeklyTimeLost * rph;

  // If you want v2 to mirror the other calculator exactly:
  const automationRate = 1.0;                // ← was 0.8; set to 1 to match
  // Keep recovery as-is (v2's recoveryRate is the same role as the other module's percentageUsedOnRevenue)
  const hoursRecoveredPerRep = weeklyTimeLostPerRep * automationRate;
  const revenueImpactPerRep =
    hoursRecoveredPerRep * 52 * rph * (recoveryRate / 100);
  const totalRevenueImpact = revenueImpactPerRep * numReps;

  const weeklyRevenueImpact = totalRevenueImpact / 52;
  const ftesEquivalent = Math.round(
    (hoursRecoveredPerRep * numReps * 52) / HOURS_PER_YEAR // use recovered time & same denominator
  );
  const growthPercentage = Math.round(
    (totalRevenueImpact / (revenuePerRep * numReps)) * 100
  );
  const aesEquivalent = Math.round(totalRevenueImpact / 750000);

  const updateProgress = function () {
    return (currentStep / totalSteps) * 100;
  };

  const goToStep = function (step) {
    if (completedSteps.includes(step)) {
      setCurrentStep(step);
      setShowResults(false);
    }
  };

  const nextStep = function () {
    if (currentStep < totalSteps) {
      var nextStepNum = currentStep + 1;

      // Safely add both the current and next step to the completedSteps list
      setCompletedSteps(function (prevCompleted) {
        const merged = new Set(prevCompleted);
        merged.add(currentStep);
        merged.add(nextStepNum);
        return Array.from(merged);
      });

      setCurrentStep(nextStepNum);
    }
  };

  const prevStep = function () {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };


  const calculateResults = function () {
    setIsLoading(true);
    setTimeout(function () {
      setIsLoading(false);
      setShowResults(true);
    }, 2000);
  };

  const resetCalculator = function () {
    setCurrentStep(1);
    setCompletedSteps([1]);
    setShowResults(false);
  };

  const getRphContext = function () {
    if (rph > industryAvg) {
      return (
        Math.round((rph / industryAvg - 1) * 100) +
        "% above industry average! "
      );
    } else if (rph < industryAvg) {
      return "Room to grow—avg is $" + industryAvg;
    }
    return "Right at industry average";
  };

  const getResultsTitle = function () {
    if (totalRevenueImpact >= 3000000) {
      return "This Is Transformational ";
    }
    if (totalRevenueImpact >= 500000) {
      return "That's Serious Money Left on the Table";
    }
    return "Small Changes, Big Impact";
  };

  const calculatorresults = {
    // "semantic" PDF fields → actual calculator values
    totalteamannualopportunity: totalRevenueImpact,
    additionalrevenueperrepannually: revenueImpactPerRep,
    hourssavedperrepperweek: hoursRecoveredPerRep,
    currenthourslostperweek: totalWeeklyTimeLost,
    currentunrealizedrevenueannually: totalRevenueImpact,
    currentequivalentftesdoingadmin: ftesEquivalent,
    revenueairecoveredrevenueannually: totalRevenueImpact,
    revenueaihourssavedperrepweek: hoursRecoveredPerRep,
    revenueaigrowthwithoutnewhires: growthPercentage,
    hourssavedperweek: hoursRecoveredPerRep * numReps,
    revenueperhour: rph,
    weeksperyear: 52,
    convertedtosellingtime: recoveryRate,
    perrep: revenueImpactPerRep,
    whatthismeans: `Your team could generate an additional ${(totalRevenueImpact / 1000000).toFixed(1)}M annually—that's equivalent to hiring ${aesEquivalent} fully-ramped AEs without the recruitment costs, ramp time, or additional overhead.`,
    timetovalue: `Most teams see measurable results within 30 days and reach full productivity gains by 90 days.`,
    weeklyopportunitycost: `Every week you wait costs you ${Math.round(weeklyRevenueImpact / 1000).toLocaleString()}K in unrealized revenue.`,
  };

  const calculatorinputs = {
    numberofreps: numReps,
    yearlyrevenueperrep: revenuePerRep,
    meetingsperweek: meetingsPerWeek,
    mintuesnotes: noteTime,
    mintuescrm: crmTime,
    mintuesemail: emailTime,
    mintuesreviewingcalls: reviewTime,
    mintuescoaching: coachingTime,
    timesavedactivities: recoveryRate,
  };

  const instanceId = useRef(`hubspot-${form_id}-${++caculatorFormInstanceCount}`);

  // inject HubSpot script once
  useEffect(() => {
    if (!form_id) return;

    const existing = document.querySelector(
      'script[src="https://js.hsforms.net/forms/embed/developer/5265893.js"]'
    );
    if (existing) return; // if YOU know it's not in the global layout, you can drop this

    const script = document.createElement("script");
    script.src = "https://js.hsforms.net/forms/embed/developer/5265893.js";
    script.defer = true;
    document.body.appendChild(script);
  }, [form_id]);

  //attach pdf download on hubspot success submission
  const resultsRef = useRef(calculatorresults);
  const inputsRef  = useRef(calculatorinputs);
  useEffect(() => {
    resultsRef.current = calculatorresults;
    inputsRef.current  = calculatorinputs;
  }, [calculatorresults, calculatorinputs]);

  // ensure we only trigger once per submission
  const hasTriggeredRef = useRef(false);

  useEffect(() => {
    if (!form_id) return;

    const container = document.getElementById(instanceId.current);
    if (!container) return;

    const triggerDownload = () => {
      if (hasTriggeredRef.current) return;
      hasTriggeredRef.current = true;
      fillForm(resultsRef.current, inputsRef.current);
      setTimeout(() => {
        hasTriggeredRef.current = false;
      }, 3000);
    };

    const wireSubmit = () => {
      const btn =
        container.querySelector('button[type="submit"]') ||
        container.querySelector('input[type="submit"]');
      if (btn && !btn.dataset.pdfWired) {
        btn.addEventListener("click", triggerDownload);
        btn.dataset.pdfWired = "1";
      }
    };

    wireSubmit();

    const mo = new MutationObserver(wireSubmit);
    mo.observe(container, { childList: true, subtree: true });

    const onHsSuccess = () => triggerDownload();
    window.addEventListener("hs-form-event:on-submission:success", onHsSuccess);

    const onMessage = (event) => {
      const d = event && event.data;
      if (
        d &&
        d.type === "hsFormCallback" &&
        d.eventName === "onFormSubmitted" &&
        String(d.id) === String(form_id)
      ) {
        triggerDownload();
      }
    };
    window.addEventListener("message", onMessage);

    return () => {
      window.removeEventListener(
        "hs-form-event:on-submission:success",
        onHsSuccess
      );
      window.removeEventListener("message", onMessage);
      mo.disconnect();
      const btn =
        container.querySelector('button[type="submit"]') ||
        container.querySelector('input[type="submit"]');
      if (btn && btn.dataset.pdfWired) {
        btn.removeEventListener("click", triggerDownload);
        delete btn.dataset.pdfWired;
      }
    };
  }, [form_id, instanceId]);


  return (
    <PageBg className="module-calculator-v2" id={anchor_id}>
      {/* Calculator */}
        <ContentMaxWidth>
          <TopResetRow show={currentStep > 1 || completedSteps.length > 1 || showResults}>
            <ResetButton onClick={resetCalculator}>
              ↻ Start Over
            </ResetButton>
          </TopResetRow>

          <CardShell>
            {!showResults && !isLoading && (
              <>
                {/* Progress Bar */}
                <ProgressTrack>
                  <ProgressBar widthPct={updateProgress()} />
                </ProgressTrack>

                <StepStatusText>
                  Step {currentStep} of {totalSteps}
                </StepStatusText>

                {/* Breadcrumb Navigation */}
                <BreadcrumbRow>
                  {[1, 2, 3].map(function (step) {
                    return (
                      <BreadcrumbButton
                        key={step}
                        onClick={function () {
                          goToStep(step);
                        }}
                        disabled={!completedSteps.includes(step)}
                        active={step === currentStep}
                        completed={completedSteps.includes(step)}
                      >
                        {step}
                      </BreadcrumbButton>
                    );
                  })}
                </BreadcrumbRow>

                {/* Step 1 */}
                {currentStep === 1 && (
                  <div>
                    <StepHeading>
                      First, tell us about your team
                    </StepHeading>
                    <StepSubheading>
                      This helps us personalize your results
                    </StepSubheading>

                    <div>
                      <FieldGroup>
                        <FieldRowLabel>
                          <label>How many sales reps do you have?</label>
                          <HelpBubble title="Include all quota-carrying reps">
                            ?
                          </HelpBubble>
                        </FieldRowLabel>

                        <RangeInput
                          type="range"
                          min="1"
                          max="500"
                          value={numReps}
                          onChange={function (e) {
                            setNumReps(Number(e.target.value));
                          }}
                        />
                        <PillValue>{numReps} reps</PillValue>
                        <HintBox>
                          Average mid-market team size: 30-75 reps
                        </HintBox>
                      </FieldGroup>

                      <FieldGroup>
                        <FieldRowLabel>
                          <label>Average yearly revenue per rep?</label>
                          <HelpBubble title="Total annual revenue ÷ number of reps">
                            ?
                          </HelpBubble>
                        </FieldRowLabel>

                        <NumberInput
                          type="number"
                          value={revenuePerRep}
                          onChange={function (e) {
                            setRevenuePerRep(Number(e.target.value));
                          }}
                        />
                        {revenuePerRep >= 650000 &&
                          revenuePerRep <= 900000 && (
                            <HintBox success>
                              ✓ $650K-$900K is typical for B2B SaaS AEs
                            </HintBox>
                          )}
                        <HintBox>
                          Industry average: $650K for SaaS AEs
                        </HintBox>
                      </FieldGroup>

                      <FieldGroup>
                        <FieldRowLabel>
                          <label>Meetings per week (per rep)?</label>
                          <HelpBubble title="Include demos, discovery calls, follow-ups">
                            ?
                          </HelpBubble>
                        </FieldRowLabel>

                        <RangeInput
                          type="range"
                          min="1"
                          max="40"
                          value={meetingsPerWeek}
                          onChange={function (e) {
                            setMeetingsPerWeek(Number(e.target.value));
                          }}
                        />
                        <PillValue>{meetingsPerWeek} meetings</PillValue>
                        <HintBox>
                          Typical range: 12-25 customer-facing meetings/week
                        </HintBox>
                      </FieldGroup>

                      <StatBlockBlue>
                        <StatSmallLabel>
                          Your Team&apos;s Revenue Per Hour:
                        </StatSmallLabel>
                        <StatBigNumber>
                          ${rph.toLocaleString()}
                        </StatBigNumber>
                        <StatContext>{getRphContext()}</StatContext>
                      </StatBlockBlue>
                    </div>

                    <div style={{ marginTop: "2rem" }}>
                      <ButtonPrimary onClick={nextStep}>
                        See What You&apos;re Losing →
                      </ButtonPrimary>
                    </div>
                  </div>
                )}

                {/* Step 2 */}
                {currentStep === 2 && (
                  <div>
                    <StepHeading>
                      Where does time go after each meeting?
                    </StepHeading>
                    <StepSubheading>
                      Let&apos;s quantify the admin burden
                    </StepSubheading>

                    <div>
                      <FieldGroup>
                        <FieldRowLabel>
                            <LabelBlock>
                              Minutes spent writing notes after each call?
                            </LabelBlock>
                            <HelpBubble title="Time documenting key points, action items, and next steps after meetings">
                              ?
                            </HelpBubble>
                        </FieldRowLabel>
                        <RangeInput
                          type="range"
                          min="0"
                          max="30"
                          value={noteTime}
                          onChange={function (e) {
                            setNoteTime(Number(e.target.value));
                          }}
                        />
                        <PillValue>{noteTime} minutes</PillValue>
                        <HintBox>
                          Manual note-taking averages 8-12 minutes
                        </HintBox>
                      </FieldGroup>

                      <FieldGroup>
                        <FieldRowLabel>
                          <LabelBlock>
                            Minutes updating your CRM per meeting?
                          </LabelBlock>
                          <HelpBubble title="Time logging activities, updating fields, and maintaining deal hygiene">
                            ?
                          </HelpBubble>
                        </FieldRowLabel>
                        <RangeInput
                          type="range"
                          min="0"
                          max="30"
                          value={crmTime}
                          onChange={function (e) {
                            setCrmTime(Number(e.target.value));
                          }}
                        />
                        <PillValue>{crmTime} minutes</PillValue>
                        <HintBox>
                          Most reps spend 5-15 minutes per update
                        </HintBox>
                      </FieldGroup>

                      <FieldGroup>
                        <FieldRowLabel>
                          <LabelBlock>
                            Minutes writing follow-up emails per meeting?
                          </LabelBlock>
                          <HelpBubble title="Time crafting personalized follow-ups, recap emails, and scheduling next steps">
                            ?
                          </HelpBubble>
                        </FieldRowLabel>
                        <RangeInput
                          type="range"
                          min="0"
                          max="45"
                          value={emailTime}
                          onChange={function (e) {
                            setEmailTime(Number(e.target.value));
                          }}
                        />
                        <PillValue>{emailTime} minutes</PillValue>
                        <HintBox>
                          Thoughtful follow-ups typically take 10-20 minutes
                        </HintBox>
                      </FieldGroup>

                      <FieldGroup>
                        <FieldRowLabel>
                          <LabelBlock>
                            Minutes reviewing call recordings per meeting?
                          </LabelBlock>
                          <HelpBubble title="Time listening back to conversations for coaching or deal strategy">
                            ?
                          </HelpBubble>
                        </FieldRowLabel>
                        <RangeInput
                          type="range"
                          min="0"
                          max="60"
                          value={reviewTime}
                          onChange={function (e) {
                            setReviewTime(Number(e.target.value));
                          }}
                        />
                        <PillValue>{reviewTime} minutes</PillValue>
                        <HintBox>
                          Most teams review 10-30% of calls thoroughly
                        </HintBox>
                      </FieldGroup>

                      <FieldGroup>
                        <FieldRowLabel>
                          <LabelBlock>
                            Minutes of coaching per rep, per week?
                          </LabelBlock>
                          <HelpBubble title="1-on-1 coaching, call reviews, skills development, and deal strategy sessions">
                            ?
                          </HelpBubble>
                        </FieldRowLabel>
                        <RangeInput
                          type="range"
                          min="0"
                          max="300"
                          value={coachingTime}
                          onChange={function (e) {
                            setCoachingTime(Number(e.target.value));
                          }}
                        />
                        <PillValue>{coachingTime} minutes</PillValue>
                        <HintBox>
                          High-performing teams: 60-90 min/week
                        </HintBox>
                      </FieldGroup>

                      <StatBlockBlue>
                        <StatSmallLabel>
                          Time Lost to Admin Per Week:
                        </StatSmallLabel>
                        <StatBigNumber>
                          {Math.round(
                            totalWeeklyTimeLost
                          ).toLocaleString()}{" "}
                          hours
                        </StatBigNumber>
                        <StatContext>
                          {"That's $" +
                            Math.round(
                              weeklyRevenueLost / 1000
                            ) +
                            "K in lost revenue per week"}
                        </StatContext>
                      </StatBlockBlue>
                    </div>

                    <ButtonRow>
                      <ButtonSecondary onClick={prevStep}>
                        ← Back
                      </ButtonSecondary>
                      <HalfButtonPrimary onClick={nextStep}>
                        Calculate My Revenue Impact →
                      </HalfButtonPrimary>
                    </ButtonRow>
                  </div>
                )}

                {/* Step 3 */}
                {currentStep === 3 && (
                  <div>
                    <StepHeading>
                      How much recovered time goes to selling?
                    </StepHeading>
                    <StepSubheading>
                      Be realistic—not all saved time becomes selling time
                    </StepSubheading>

                    <div>
                      <FieldGroup>
                        <FieldRowLabel>
                          <label>
                            Percentage of time saved used on
                            revenue-generating activities
                          </label>
                          <HelpBubble title="We recommend 75%">
                            ?
                          </HelpBubble>
                        </FieldRowLabel>

                        <RangeInput
                          type="range"
                          min="0"
                          max="100"
                          value={recoveryRate}
                          onChange={function (e) {
                            setRecoveryRate(Number(e.target.value));
                          }}
                        />
                        <PillValue>{recoveryRate}%</PillValue>
                        <HintBox>
                          Based on data from 10,000+ revenue teams, 75% is a
                          realistic target
                        </HintBox>
                      </FieldGroup>

                      <div
                        style={{
                          backgroundColor: "#f3f4f6",
                          padding: "1.25rem",
                          borderRadius: "0.5rem"
                        }}
                      >
                        <p
                          style={{
                            fontSize: ".875rem",
                            color: "#374151",
                            lineHeight: 1.6
                          }}
                        >
                          <strong>Why not 100%?</strong> Not all saved time
                          becomes selling time (meetings, training, breaks).
                          We use a conservative estimate to show realistic
                          outcomes. Most teams find 75% is achievable with
                          strong adoption.
                        </p>
                      </div>
                    </div>

                    <ButtonRow>
                      <ButtonSecondary onClick={prevStep}>
                        ← Back
                      </ButtonSecondary>
                      <HalfButtonPrimary onClick={calculateResults}>
                        Show Me My Results
                      </HalfButtonPrimary>
                    </ButtonRow>
                  </div>
                )}
              </>
            )}

            {/* Loading */}
            {isLoading && (
              <LoadingWrap>
                <Spinner />
                <LoadingText>
                  Calculating your revenue impact...
                </LoadingText>
              </LoadingWrap>
            )}

            {/* Results */}
            {showResults && (
              <div>
                <ResultsHeaderWrap>
                  <ResultsHeadline>{getResultsTitle()}</ResultsHeadline>
                  <AdjustInputsButton onClick={resetCalculator}>
                    ← Adjust your inputs
                  </AdjustInputsButton>
                </ResultsHeaderWrap>

                {/* Comparison Cards */}
                <TwoColGrid>
                  <CardCurrentState>
                    <TinyEyebrowGray>CURRENT STATE</TinyEyebrowGray>

                    <div>
                      <InnerMetricBlock>
                        <MetricLabel>Hours Lost Per Week</MetricLabel>
                        <MetricValue>
                          {Math.round(
                            totalWeeklyTimeLost
                          ).toLocaleString()}
                        </MetricValue>
                      </InnerMetricBlock>

                      <InnerMetricBlock>
                        <MetricLabel>Unrealized Revenue Annually</MetricLabel>
                        <MetricValue>
                          ${(totalRevenueImpact / 1000000).toFixed(1)}M
                        </MetricValue>
                      </InnerMetricBlock>

                      <InnerMetricBlock>
                        <MetricLabel>
                          Equivalent FTEs Doing Admin
                        </MetricLabel>
                        <MetricValue>{ftesEquivalent}</MetricValue>
                      </InnerMetricBlock>
                    </div>
                  </CardCurrentState>

                  <CardRevenueAI>
                    <TinyEyebrowBlue>WITH REVENUE AI</TinyEyebrowBlue>

                    <div>
                      <InnerMetricBlock>
                        <MetricLabel>
                          Hours Saved Per Rep/Week
                        </MetricLabel>
                        <MetricValue>
                          {hoursRecoveredPerRep.toFixed(1)}
                        </MetricValue>
                      </InnerMetricBlock>

                      <InnerMetricBlock>
                        <MetricLabel>
                          Recovered Revenue Annually
                        </MetricLabel>
                        <MetricValue>
                          ${(totalRevenueImpact / 1000000).toFixed(1)}M
                        </MetricValue>
                      </InnerMetricBlock>

                      <InnerMetricBlock>
                        <MetricLabel>
                          Growth Without New Hires
                        </MetricLabel>
                        <MetricValue>{growthPercentage}%</MetricValue>
                      </InnerMetricBlock>
                    </div>
                  </CardRevenueAI>
                </TwoColGrid>

                {/* Impact Cards */}
                <ThreeColGrid>
                  <ImpactCard>
                    <ImpactNumber>
                      $
                      {Math.round(
                        revenueImpactPerRep
                      ).toLocaleString()}
                    </ImpactNumber>
                    <ImpactLabel>
                      Additional Revenue Per Rep Annually
                    </ImpactLabel>
                  </ImpactCard>

                  <ImpactCard>
                    <ImpactNumber>
                      {hoursRecoveredPerRep.toFixed(1)}
                    </ImpactNumber>
                    <ImpactLabel>
                      Hours Saved Per Rep Per Week
                    </ImpactLabel>
                  </ImpactCard>

                  <ImpactCard>
                    <ImpactNumber>
                      $
                      {Math.round(
                        totalRevenueImpact
                      ).toLocaleString()}
                    </ImpactNumber>
                    <ImpactLabel>
                      Total Team Annual Opportunity
                    </ImpactLabel>
                  </ImpactCard>
                </ThreeColGrid>

                {/* Breakdown */}
                <BreakdownBox>
                  <BreakdownTitle>
                    Per Rep Calculation Breakdown:
                  </BreakdownTitle>
                  <BreakdownContent>
                    <strong>{hoursRecoveredPerRep.toFixed(1)}</strong> hours
                    saved per week
                    <br />×{" "}
                    <strong>${Math.round(rph)}</strong> revenue per hour
                    <br />× <strong>52</strong> weeks per year
                    <br />× <strong>{recoveryRate}%</strong> converted to
                    selling time
                    <br />
                    ─────────────────────
                    <br />=
                    <strong>
                      {" "}
                      $
                      {Math.round(
                        revenueImpactPerRep
                      ).toLocaleString()}
                    </strong>{" "}
                    per rep
                  </BreakdownContent>
                </BreakdownBox>

                {/* Insights */}
                <InsightsWrap>
                  <InsightRow>
                    <InsightHeadline>What This Means:</InsightHeadline>
                    <InsightBody>
                      Your team could generate an additional{" "}
                      {(totalRevenueImpact / 1000000).toFixed(1)}M
                      annually—that&apos;s equivalent to hiring{" "}
                      {aesEquivalent} fully-ramped AEs without the
                      recruitment costs, ramp time, or additional
                      overhead.
                    </InsightBody>
                  </InsightRow>

                  <InsightRow>
                    <InsightHeadline>Time to Value:</InsightHeadline>
                    <InsightBody>
                      Most teams see measurable results within 30 days
                      and reach full productivity gains by 90 days.
                    </InsightBody>
                  </InsightRow>

                  <InsightRow>
                    <InsightHeadline>Weekly Opportunity Cost:</InsightHeadline>
                    <InsightBody>
                      Every week you wait costs you $
                      {Math.round(
                        weeklyRevenueImpact / 1000
                      ).toLocaleString()}
                      K in unrealized revenue.
                    </InsightBody>
                  </InsightRow>
                </InsightsWrap>

                {!form_id && (
                      <div style={btnContainer}>
                        <button
                          style={btnStyle}
                          type="button"
                          onClick={() => fillForm(calculatorresults, calculatorinputs)}
                        >
                          Download Results
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {form_id && (
                  <FormWrapper visible={showResults}>
                    <FormContainer
                      id={instanceId.current}
                      className="hs-form-html"
                      data-region="na1"
                      data-portal-id="5265893"
                      data-form-id={form_id}
                    />
                  </FormWrapper>
                )}
          </CardShell>
        </ContentMaxWidth>
    </PageBg>
  );
}

/* Layout / wrappers */
const PageBg = createComponent(() => ({
  overflow: "hidden",
  extend: [
      fluid("paddingH", gutters.default),
      fluid("paddingV", gutters.verticalXLarge)
  ]
}), "div", ["id"]);

const TopResetRow = createComponent(p => ({
  display: p.show ? "flex" : "none",
  justifyContent: "flex-end",
  marginBottom: "1rem",
}), "div");

const ResetButton = createComponent(() => ({
  padding: "9px 18px",
  borderWidth: "2px",
  borderStyle: "solid",
  borderColor: colors.blue,
  color: colors.blue,
  borderRadius: "12px",
  fontWeight: 600,
  backgroundColor: "transparent",
  cursor: "pointer",
  transition: "background-color .15s, color .15s",
  ":hover": { backgroundColor: colors.blue, color: colors.white },
}), "button", ["onClick"]);

const CardShell = createComponent(() => ({
  backgroundColor: colors.white,
  borderRadius: "12px",
  boxShadow: "0 25px 50px -12px rgba(0,0,0,.25),0 10px 15px -3px rgba(0,0,0,.1)",
  padding: "2rem",
}), "div");

/* Progress */
const ProgressTrack = createComponent(() => ({
  width: "100%", height: 12, backgroundColor: colors.blue,
  borderRadius: "9999px", marginBottom: "2rem",
}), "div");

const ProgressBar = createComponent(p => ({
  height: "100%", backgroundColor: colors.blue600 || "#2563eb",
  borderRadius: "9999px", transition: "all .5s",
  width: (p.widthPct || 0) + "%",
}), "div"); // NOTE: do NOT pass widthPct

const StepStatusText = createComponent(() => ({
  textAlign: "center", color: colors.darkGrey, marginBottom: 12,
  ...textStyles.paragraphSmall,
}), "div");

/* Breadcrumbs */
const BreadcrumbRow = createComponent(() => ({
  display: "flex", justifyContent: "center", gap: 12, marginBottom: 24,
}), "div");

const BreadcrumbButton = createComponent(p => ({
  width: 36, height: 36, borderRadius: "9999px",
  display: "flex", alignItems: "center", justifyContent: "center",
  fontWeight: 600, fontSize: 18,
  cursor: p.disabled ? "not-allowed" : "pointer",
  opacity: p.disabled ? 0.5 : 1,
  backgroundColor: p.active
    ? colors.blue
    : p.completed ? colors.green : colors.darkGrey,
  color: p.active || p.completed ? "#fff" : colors.lightGrey,
  transition: "transform .15s",
  ":hover": p.completed && !p.active ? { transform: "scale(1.10)" } : {},
}), "button", ["onClick", "disabled", "active", "completed"]);

/* Headings */
const StepHeading = createComponent(() => ({
  textAlign: "center", fontWeight: 700,
  ...textStyles.marqueeEyebrow,
  ...fonts.ambitBold,
}), "h2");

const StepSubheading = createComponent(() => ({
  textAlign: "center", marginBottom: 24,
  ...textStyles.paragraphSmall
}), "p");

const ResultsHeaderWrap = createComponent(() => ({
  textAlign: "center", marginBottom: 24,
}), "div");

const ResultsHeadline = createComponent(() => ({
  marginBottom: "1rem",
  ...textStyles.heading2,
}), "h2");

const AdjustInputsButton = createComponent(() => ({
  background: "none", border: "none", color: colors.blue,
  fontWeight: 600, cursor: "pointer", textDecoration: "none",
  ":hover": { textDecoration: "underline" },
}), "button", ["onClick"]);

/* Fields */
const FieldGroup = createComponent(() => ({ marginBottom: 24 }), "div");

const FieldRowLabel = createComponent(() => ({
  display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6,
  ...textStyles.paragraphSmall,

}), "div");

const HelpBubble = createComponent(() => ({
  fontSize: 12, backgroundColor: colors.lightGrey, width: 24, height: 24,
  borderRadius: "9999px", display: "flex", alignItems: "center", justifyContent: "center",
  cursor: "help",
}), "span", ["title", "aria-label"]);

const LabelBlock = createComponent(() => ({
  marginBottom: 12, display: "block",
}), "label");

const RangeInput = createComponent(() => ({
  width: "100%", height: 6, backgroundColor: colors.grey,
  borderRadius: 6, WebkitAppearance: "none", appearance: "none",
  cursor: "pointer", outline: "none",
}), "input", ["type", "min", "max", "value", "onChange"]);

const NumberInput = createComponent(() => ({
  width: "100%", padding: "12px 18px", borderWidth: "2px", borderStyle: "solid",
  borderColor: colors.darkGrey, borderRadius: 6,
  outline: "none", ":focus": { borderColor: colors.blue },
}), "input", ["type", "value", "onChange"]);

const PillValue = createComponent(() => ({
  display: "inline-block", backgroundColor: colors.blue, color: "#fff",
  padding: "9px 18px", borderRadius: 24, marginTop: 12,
  fontWeight: 600, fontSize: 12,
}), "div");

const HintBox = createComponent( p => ({
  fontSize: 14, color: colors.black,
  backgroundColor: p.success ? "#ecfdf5" : colors.grey,
  marginTop: 6, padding: 12, borderRadius: 6,
  display: "flex", alignItems: "center", fontWeight: 500,
}), "div", ["success"]);

/* Stat blocks */
const StatBlockBlue = createComponent(() => ({
  backgroundColor: "#EBEEF3",
  padding: 18, borderRadius: "12px", textAlign: "center", borderWidth: 1, borderStyle: "solid", borderColor: colors.blue,
}), "div");

const StatSmallLabel = createComponent(() => ({
   marginBottom: 12,
  ...textStyles.paragraphSmall,
}), "div");

const StatBigNumber = createComponent(() => ({
  color: colors.blue,
  ...textStyles.heading2,
  ...fonts.ambitBold,
}), "div");

const StatContext = createComponent(() => ({
  marginTop: 12,
  ...textStyles.paragraphSmall,
}), "div");

/* Buttons */
const ButtonPrimary = createComponent(() => ({
  width: "100%", backgroundColor: colors.blue, color: "#fff",
  padding: 18, borderRadius: 12, fontWeight: 600,
  textAlign: "center", border: "none", cursor: "pointer",
  transition: "background-color .15s", ":hover": { backgroundColor: colors.blueActive },
  ...textStyles.paragraphNormal,
}), "button", ["onClick", "type"]);

const ButtonRow = createComponent(() => ({
  display: "flex", gap: 12, marginTop: 24,
}), "div");

const ButtonSecondary = createComponent(() => ({
  flex: 1, backgroundColor: colors.white,
  border: "2px solid " + colors.blue, color: colors.blue,
  padding: 18, borderRadius: 12, fontWeight: 600,
  textAlign: "center", cursor: "pointer",
  transition: "background-color .15s", ":hover": { backgroundColor: "#f9fafb" },
  ...textStyles.paragraphNormal,
}), "button", ["onClick", "type"]);

const HalfButtonPrimary = createComponent(() => ({
  flex: 1, width: "100%", backgroundColor: colors.blue, color: colors.white,
  padding: 18, borderRadius: 12, fontWeight: 600,
  textAlign: "center", border: "none", cursor: "pointer",
  transition: "background-color .15s", ":hover": { backgroundColor: colors.blueActive },
  ...textStyles.paragraphNormal,
}), "button", ["onClick", "type"]);

/* Loading */
const LoadingWrap = createComponent(() => ({
  textAlign: "center", padding: "4rem 0",
}), "div");

const Spinner = function Spinner() {
  const { renderer, css } = useFela();

  // define keyframes via renderer (no fela import)
  const spin = renderer.renderKeyframe(() => ({
    from: { transform: "rotate(0deg)" },
    to:   { transform: "rotate(360deg)" },
  }));

  const loadingSpinner = css({
    width: 36,
    height: 36,
    borderWidth: 8,
    borderStyle: "solid",
    borderColor: colors.grey,
    borderTopColor: colors.blue,
    borderRadius: "9999px",
    animationName: spin,
    animationDuration: ".5s",
    animationTimingFunction: "linear",
    animationIterationCount: "infinite",
    margin: "0 auto 12px",
    willChange: "transform",
  });

  return <div className={loadingSpinner} />;
};


const LoadingText = createComponent(() => ({ color: "#4b5563" }), "p");

/* Results grids & cards */
const TwoColGrid = createComponent(() => ({
  display: "grid", gridTemplateColumns: "1fr", gap: 18, marginBottom: 24,
  "@media(min-width:768px)": { gridTemplateColumns: "1fr 1fr" },
}), "div");

const CardCurrentState = createComponent(() => ({
  backgroundColor: colors.lightGrey,
  padding: 24, borderRadius: 12, border: "2px solid " + colors.darkGrey,
}), "div");

const CardRevenueAI = createComponent(() => ({
  backgroundColor: "#EBEEF3",
  padding: 24, borderRadius: 12, border: "2px solid " + (colors.blue),
}), "div");

const TinyEyebrowGray = createComponent(() => ({
  marginBottom: 12,
  ...textStyles.paragraphSmall,
}), "div");

const TinyEyebrowBlue = createComponent(() => ({
  marginBottom: 12, color: colors.blue,
  ...textStyles.paragraphSmall,
}), "div");

const InnerMetricBlock = createComponent(() => ({ marginBottom: 12 }), "div");
const MetricLabel      = createComponent(() => ({ ...textStyles.paragraphSmall }), "div");
const MetricValue      = createComponent(() => ({ ...textStyles.paragraphNormal, fontWeight: 600 }), "div");

const ThreeColGrid = createComponent(() => ({
  display: "grid", gridTemplateColumns: "1fr", gap: "1.5rem", marginBottom: "2.5rem",
  "@media(min-width:768px)": { gridTemplateColumns: "1fr 1fr 1fr" },
}), "div");

const ImpactCard   = createComponent(() => ({
  backgroundColor: "#fff", borderRadius: ".75rem",
  boxShadow: "0 10px 15px -3px rgba(0,0,0,.1),0 4px 6px -2px rgba(0,0,0,.05)",
  padding: "1.5rem", textAlign: "center",
}), "div");

const ImpactNumber = createComponent(() => ({
  fontSize: "2.25rem", fontWeight: 700, lineHeight: 1.1, color: colors.blue600 || "#2563eb", marginBottom: ".5rem",
}), "div");

const ImpactLabel  = createComponent(() => ({ color: "#4b5563" }), "div");

/* Breakdown & insights */
const BreakdownBox = createComponent(() => ({
  backgroundColor: "#f3f4f6", padding: "2rem", borderRadius: ".75rem", marginBottom: "2.5rem",
}), "div");

const BreakdownTitle = createComponent(() => ({
  fontWeight: 700, fontSize: "1.25rem", marginBottom: "1rem",
}), "h3");

const BreakdownContent = createComponent(() => ({
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  color: "#1f2937", lineHeight: 1.7, fontSize: ".95rem",
}), "div");

const InsightsWrap = createComponent(() => ({
  backgroundColor: "#fff", borderRadius: ".75rem",
  boxShadow: "0 10px 15px -3px rgba(0,0,0,.1),0 4px 6px -2px rgba(0,0,0,.05)",
  padding: "2rem", marginBottom: "2.5rem", display: "grid", rowGap: "1rem",
}), "div");

const InsightRow = createComponent(() => ({
  borderLeft: "4px solid " + (colors.blue600 || "#2563eb"),
  paddingLeft: "1rem", paddingTop: ".5rem", paddingBottom: ".5rem",
  backgroundColor: "#f9fafb",
}), "div");

const InsightHeadline = createComponent(() => ({ fontWeight: 700, marginBottom: ".5rem", color: "#000" }), "div");
const InsightBody     = createComponent(() => ({ color: "#374151", lineHeight: 1.5 }), "p");

/* Email CTA */
const FormWrapper = createComponent(
  (p) => ({
    marginTop: 30,
    display: p.visible ? "block" : "none",
  }),
  "div",
  ["visible"]
);

const FormContainer = createComponent(
  () => ({
    width: "100%",
    "& .hsfc-Step__Content": { padding: "0 !important", display: "flex" },
    "& .hsfc-Row": { width: "auto !important", flexGrow: 1, marginRight: 20},
    "& .hsfc-Step__Content .hsfc-NavigationRow": { marginTop: 0},
  }),
  "div",
  ["id", "className", "data-region", "data-portal-id", "data-form-id"]
);

const btnContainer = {
    marginTop: 30,
    textAlign: "center",
};

const btnStyle = {
    backgroundColor: "#3B58F5",
    borderRadius: 600,
    padding: "16px",
    paddingLeft: "32px",
    paddingRight: "32px",
    color: "#fff",
    textAlign: "center",
    border: 0,
    cursor: "pointer",

    ":hover": {
      opacity: .7,
    }
};
